
import Vue, { PropType } from "vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import AssessmentDetailsScreenshoot from "./AssessmentDetailsScreenshoot.vue";
import { mapGetters, mapMutations } from "vuex";
import { SITE_DIRECTION } from "@/store/modules/common/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import { download_file } from "@/utils/global";
import { ROOT_ERROR } from "@/store/modules/root/constants";

export default Vue.extend({
  name: "AssessmentViewDetails",
  components: {
    AssessmentDetailsScreenshoot
  },
  data() {
    return {
      dialog: true,
      panel: [],
      selected_screenshoot_index: 0,
      filtered_questions: [] as AppAssessments.AssessmentResultDetails[],
      is_view_screenshoot: false,
      audio_array: [] as boolean[],
      audio_progress: {} as Record<
        number,
        { current: number; duration: number }
      >
    };
  },
  mounted() {
    if (!this.is_annotataion) {
      if (!this.payload.result) return;
      const details = this.payload.result.details;
      this.filtered_questions =
        details?.filter(
          (item) =>
            item.question.question_options?.filter ===
            AppAssessments.QuestionFilter.SPEAKING
        ) ?? [];
      this.filtered_questions.forEach((_, i) => {
        this.audio_array.push(false);
        this.$set(this.audio_progress, i, { current: 0, duration: 0 });
      });
    }
  },
  props: {
    title: {
      type: String
    },
    payload: {
      type: Object as PropType<AppAssessments.AssessmentResults>
    },
    is_annotataion: {
      type: Boolean
    }
  },
  watch: {
    panel() {
      this.stop_audio_and_video();
    },
    dialog(value) {
      if (!value) {
        this.stop_audio_and_video();
        this.$emit("close");
      }
    }
  },
  computed: {
    ...mapGetters("common", {
      get_site_direction: SITE_DIRECTION
    }),
    SiteDirections() {
      return SiteDirections;
    },
    has_screenshots(): boolean {
      return Boolean(
        this.payload?.urls?.screen_shots &&
          this.payload.urls.screen_shots.length > 0
      );
    }
  },
  methods: {
    ...mapMutations({
      set_root_error: ROOT_ERROR
    }),
    get_audio_el(index: number): HTMLAudioElement | null {
      const ref = this.$refs["audioElement_" + index] as
        | HTMLAudioElement
        | HTMLAudioElement[]
        | undefined;
      if (!ref) return null;
      return (Array.isArray(ref) ? ref[0] : ref) as HTMLAudioElement;
    },
    get_audio_src(index: number): string {
      const item = this.filtered_questions[index];
      return item?.answer?.answer_url?.location ?? "";
    },
    get_progress_pct(index: number): number {
      const p = this.audio_progress[index];
      if (!p || p.duration <= 0) return 0;
      return Math.min(100, (p.current / p.duration) * 100);
    },
    format_voice_time(index: number): string {
      const p = this.audio_progress[index];
      if (!p) return "0:00 / 0:00";
      const cur = Math.floor(p.current);
      const dur = Math.floor(p.duration) || cur;
      return `${this.fmt_sec(cur)} / ${this.fmt_sec(dur)}`;
    },
    fmt_sec(s: number): string {
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${m}:${sec < 10 ? "0" : ""}${sec}`;
    },
    on_audio_timeupdate(index: number) {
      const el = this.get_audio_el(index);
      if (!el) return;
      this.$set(this.audio_progress, index, {
        current: el.currentTime,
        duration: el.duration && isFinite(el.duration) ? el.duration : 0
      });
    },
    on_audio_loaded(index: number) {
      const el = this.get_audio_el(index);
      if (!el) return;
      const duration = el.duration && isFinite(el.duration) ? el.duration : 0;
      this.$set(this.audio_progress, index, {
        current: el.currentTime,
        duration
      });
    },
    on_seek(index: number, event: MouseEvent) {
      const el = this.get_audio_el(index);
      const wrap = (event.currentTarget as HTMLElement).querySelector(
        ".voice-progress-track"
      ) as HTMLElement;
      if (!el || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const pct = Math.max(
        0,
        Math.min(1, (event.clientX - rect.left) / rect.width)
      );
      el.currentTime = pct * el.duration;
      this.$set(this.audio_progress, index, {
        current: el.currentTime,
        duration: el.duration
      });
    },
    get_question_text(
      item: AppAssessments.AssessmentResultDetails,
      index: number
    ): string {
      return this.format_question_text(item.question.question_text, index);
    },
    format_question_text(rawText: string, index: number): string {
      if (!rawText) {
        return `${index + 1}. `;
      }
      const withoutTags = rawText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ");
      return `${index + 1}. ${withoutTags.trim()}`;
    },
    on_click(index: number) {
      const el = this.get_audio_el(index);
      if (!el) return;

      for (let i = 0; i < this.filtered_questions.length; i++) {
        const a = this.get_audio_el(i);
        if (a && i !== index) {
          a.pause();
          this.$set(this.audio_array, i, false);
        }
      }

      if (el.paused) {
        el.play().catch((err) => {
          // eslint-disable-next-line no-console
          console.error("Failed to play audio", err);
        });
        this.$set(this.audio_array, index, true);
      } else {
        el.pause();
        this.$set(this.audio_array, index, false);
      }
    },
    async on_download_file(index: number) {
      try {
        const selectedAns = this.filtered_questions[index];
        let url: string | undefined;

        if (selectedAns?.answer?.answer_url?.location) {
          url = selectedAns.answer.answer_url.location;
        } else if (
          selectedAns?.answer?.answer_url &&
          typeof selectedAns.answer.answer_url === "string"
        ) {
          url = selectedAns.answer.answer_url;
        }

        if (!url) {
          const audioEl = this.get_audio_el(index);
          url = audioEl
            ? (audioEl.currentSrc || audioEl.src || "").trim()
            : undefined;
        }

        if (url) {
          await download_file(url);
        } else {
          this.set_root_error(this.$t("errors.file-download-failed") as string);
        }
      } catch {
        this.set_root_error(this.$t("errors.file-download-failed") as string);
      }
    },
    view_screenshoots(index: number) {
      this.selected_screenshoot_index = index;
      this.is_view_screenshoot = true;
    },
    is_audio_playing(index: number): boolean {
      return this.audio_array[index] || false;
    },
    on_audio_end(index: number) {
      this.$set(this.audio_array, index, false);
    },
    stop_audio_and_video() {
      this.filtered_questions.forEach((_, i) => {
        const audio = this.get_audio_el(i);
        if (audio) audio.pause();
      });
      this.audio_array = this.audio_array.map(() => false);
      const camera_recording = this.$refs
        .videoElementCamera as HTMLVideoElement;
      if (camera_recording) camera_recording.pause();
      const screen_recording = this.$refs
        .videoElementScreen as HTMLVideoElement;
      if (screen_recording) screen_recording.pause();
    }
  }
});
